import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import secureLocalStorage, { clearSessionKeys } from "../utils/secureLocalStorage";
import { OrganizationsRef, User } from "../models/User";
import alertService from "../components/alert/alertService";
import { t } from "i18next";
import { STORAGE_KEYS } from "../constants";

// Flag สำหรับ Refresh Token logic
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

// session จบแล้ว (refresh ตายจริง หรือ server ปิด session นี้): ล้างเฉพาะ key ของ session แบบเดียวกับตอนกดออก แล้วเด้งไป login
const terminateSession = (err: unknown): Promise<never> => {
  clearSessionKeys();
  globalThis.location.href = "/login";
  return Promise.reject(err);
};

const PUBLIC_PATHS = [
  "/v1/users/login",
  "/v1/users/forget-password",
  "/v1/users/reset-password",
  "/v1/users/unlock",
  // Partner SSO exchange: handoff code คือตัว auth เอง, ไม่มี user token ใน storage ตอนเรียก
  "/v1/app/sso/exchange",
];

// activate has a dynamic segment: /v1/users/{id}/activate
const isPublicPath = (url: string) =>
  PUBLIC_PATHS.some((path) => url === path || url.startsWith(path + "?")) ||
  url.endsWith("/activate");

const apiClient: AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
});

const externalApiBaseUrl = process.env.REACT_APP_EXTERNAL_API_URL;
if (!externalApiBaseUrl) {
  // interpolating an unset var yielded the literal "undefined", so calls silently went to a relative path
  // eslint-disable-next-line no-console
  console.error("[axios] REACT_APP_EXTERNAL_API_URL is not set — SSO calls will not reach the External API");
}

export const externalApiClient: AxiosInstance = axios.create({
  baseURL: externalApiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Fail closed: without a base URL axios resolves these against the app's own origin, sending the link token, fb code and Bearer to the wrong server.
externalApiClient.interceptors.request.use((config) => {
  if (!externalApiBaseUrl) {
    return Promise.reject(new Error("REACT_APP_EXTERNAL_API_URL is not set"));
  }
  return config;
});

// TOKEN_REVOKED: carboncal has ended the session this request carried, and a refresh cannot revive it.
externalApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<any>) => {
    const headers: any = error.config?.headers;
    const carriedSession = Boolean(headers?.Authorization ?? headers?.authorization);
    if (error.response?.status === 401 && error.response.data?.code === "TOKEN_REVOKED" && carriedSession) {
      return terminateSession(error);
    }
    return Promise.reject(error);
  },
);

// =====================
// Request Interceptor
// =====================
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const user = secureLocalStorage.getJSON<User>(STORAGE_KEYS.USER);

    if (!user && !isPublicPath(config.url ?? "")) {
      window.location.href = "/login";
      return Promise.reject(new Error("Unauthenticated"));
    }

    const organization =
      secureLocalStorage.getJSON<OrganizationsRef>(STORAGE_KEYS.ORG);
    if (organization?.id) {
      config.headers["X-CARBONCAL-ORGID"] = `${organization.id}`;
    }

    if (user?.accessToken) {
      config.headers["Authorization"] = `Bearer ${user.accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// =====================
// Response Interceptor
// =====================
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<any>) => {
    // แก้ไข: ถ้า checkUserNotFound ทำงาน ให้หยุด flow ทันที
    try {
      await checkUserNotFound(error);
    } catch (e) {
      return Promise.reject(e);
    }

    const originalRequest: any = error.config;

    const status = error.response?.status;

    const code = error.response?.data.code;

    // TOKEN_REVOKED: the session's row is gone, or a change to the user's authorization blacklisted this token; both end it here
    if (status === 401 && code === "TOKEN_REVOKED") {
      return terminateSession(error);
    }

    if (status === 401 && !originalRequest._retry) {
      if (code === "TOKEN_EXPIRED") {
        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          const attemptedRefreshToken = secureLocalStorage.getJSON<User>(
            STORAGE_KEYS.USER,
          )?.refreshToken;

          refreshPromise = refreshToken()
            .catch((err) => {
              const rotatedToken = secureLocalStorage.getJSON<User>(
                STORAGE_KEYS.USER,
              )?.refreshToken;

              // อีกแท็บอาจหมุน refresh token สำเร็จไปแล้ว ลองซ้ำหนึ่งครั้งก่อนตัดสินว่า session ตายจริง
              if (rotatedToken && rotatedToken !== attemptedRefreshToken) {
                return refreshToken().catch(terminateSession);
              }

              return terminateSession(err);
            })
            .finally(() => {
              isRefreshing = false;
              refreshPromise = null; // เคลียร์ Promise เมื่อเสร็จสิ้น (ทำที่นี่ที่เดียว)
            });
        }

        try {
          // รอให้ Refresh เสร็จ
          if (refreshPromise) {
            await refreshPromise;
          }

          // อ่าน Token ใหม่มาเตรียมไว้
          const user = secureLocalStorage.getJSON<User>(STORAGE_KEYS.USER);

          if (user?.accessToken) {
            // ยัด Token ใหม่ใส่ Header ของ Request ที่จะยิงซ้ำโดยตรง
            originalRequest.headers[
              "Authorization"
            ] = `Bearer ${user.accessToken}`;
          }
          // -----------------------------

          // ยิง Request เดิมซ้ำ (Interceptor จะทำงานและใส่ Token ใหม่ให้อัตโนมัติ)
          return apiClient(originalRequest);
        } catch (refreshError) {
          // ถ้า Refresh ล้มเหลว ให้ Reject Request เดิมไปเลย
          return Promise.reject(refreshError);
        }
      }
    }

    // caller opts out of the global alert to show its own (non-blocking) UX instead
    const suppressGlobalAlert = !!(error as any).config?._suppressGlobalAlert;

    if (status === 500) {
      if (suppressGlobalAlert) {
        return Promise.reject(error);
      }
      await alertService.error(`${t("Internal-Server-Error")}`);
      (error as any)._handled = true;
      return Promise.reject(error);
    }

    // Retryable by definition, so it must not read as a server fault or a dead session.
    if (status === 503) {
      // a caller that words some 503 codes itself lists them; every other 503 gets the generic notice
      const own503Codes: string[] = (error as any).config?._own503Codes ?? [];
      if (suppressGlobalAlert || own503Codes.includes(error.response?.data?.code)) {
        return Promise.reject(error);
      }
      await alertService.info(`${t("Service-Unavailable-Error")}`);
      (error as any)._handled = true;
      return Promise.reject(error);
    }

    if (status === 504) {
      if (suppressGlobalAlert) {
        return Promise.reject(error);
      }
      const isLongRunning = !!(error as any).config?._longRunning;
      if (isLongRunning) {
        await alertService.info(`${t("Report-Generation-In-Progress")}`);
      } else {
        await alertService.error(`${t("Gateway-Timeout-Error")}`);
      }
      (error as any)._handled = true;
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export const isHandledError = (error: unknown): boolean =>
  !!(error as any)?._handled;

// =====================
// Helper: ตรวจ user หาย
// =====================
const checkUserNotFound = async (error: AxiosError<any>) => {
  const code = error.response?.data?.code;

  if (code === "USER_NOT_FOUND_DURING_AUTHENTIFICATION") {
    await alertService.error(`${t("User-Not-Found")}`);
    secureLocalStorage.clear();
    window.location.href = "/login";
    // แก้ไข: Throw error เพื่อหยุดการทำงานของ Interceptor ต่อ
    throw new Error("User not found, redirecting to login");
  }
};

// =====================
// Helper: Refresh Token
// =====================
const refreshToken = async (): Promise<void> => {
  const user = secureLocalStorage.getJSON<User>(STORAGE_KEYS.USER);

  if (!user || !user.refreshToken) {
    throw new Error("No refresh token available");
  }

  // สร้าง Axios instance ใหม่เพื่อเลี่ยง Interceptor วนลูป
  const refreshClient = axios.create({
    baseURL: `${process.env.REACT_APP_API_URL}`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${user.refreshToken}`,
    },
  });

  const response = await refreshClient.post(`/v1/users/${user.refId}/refresh`);

  if (response.status === 200 && response.data?.results?.user) {
    // Merge user ใหม่กับข้อมูลเดิม (เผื่อมี field อื่นที่ไม่ได้ส่งกลับมา)
    const newUser = { ...user, ...response.data.results.user };
    secureLocalStorage.setJSON(STORAGE_KEYS.USER, newUser);
  } else {
    throw new Error("Invalid refresh response");
  }
};

export default apiClient;
